import { flow } from '@1x2network/flow'
import { BasicLayout, OrientationMap, Orientor } from '@1x2network/layout'
import {
    applySpecification,
    ContainerSpecification,
    createSpine,
    createSprite,
    Specification,
    Spine,
    SpineSpecification,
    SpriteSpecification,
} from '@1x2network/pixi_assets'
import { LoadingButton, LoadingButtonSpec, Splash, SplashSpecification } from '@1x2network/pixi_splash'
import gsap from 'gsap'
import { Container, Sprite } from 'pixi.js'
import { SignalConnection } from 'typed-signals'
import { AntebetSplashPageSpec, SplashPage, SplashPageSpec } from './SplashPage'

/**
 * Splash screen for Train Strike Hold and Win
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class TrainStrikeHAWSplash extends Splash<SplashSpec> {
    private continue: LoadingButton
    private continueOrientor: Orientor<LoadingButtonSpec>
    private layout: BasicLayout
    private backgroundSprite: Sprite
    private backgroundSpine: Spine
    private logo: Spine
    private logoOrientor: Orientor<ContainerSpecification>
    private onResizeConnection: SignalConnection

    constructor(options: { parent?: Container; layout: BasicLayout }) {
        const spec = Specification.get('splash') as SplashSpec
        // show instructions for antebet on page
        const pageSpec = window.clientAPI.regulatory.disableAntebet ? spec.page : spec.antebetPage

        super({
            spec,
            parent: options.parent,
            pages: [new SplashPage({ spec: pageSpec, layout: options.layout })],
        })

        this.layout = options.layout

        this.backgroundSprite = createSprite({ spec: this.specification.background, parent: this })
        this.backgroundSpine = createSpine({ spec: this.specification.glow, parent: this.backgroundSprite })

        this.continue = new LoadingButton({ spec: this.specification.continue.landscape, parent: this })
        this.continueOrientor = new Orientor({
            layout: options.layout,
            property: this.specification.continue,
            onOrient: (active) => applySpecification(this.continue, active),
        })

        this.logo = createSpine({ spec: this.specification.logo, parent: this })
        this.logoOrientor = new Orientor({
            layout: options.layout,
            property: this.specification.logo,
            onOrient: (active) => applySpecification(this.logo, active),
        })

        this.onResizeConnection = options.layout.onResize.connect(this.onResize.bind(this))
    }

    private onResize(): void {
        const size = this.specification.background.size
        const { orientation, left, right, top, bottom } = this.layout
        const sprite = this.backgroundSprite

        applySpecification(sprite, this.specification.background[orientation])
        const anchorX = gsap.utils.mapRange(-size.width / 2, size.width / 2, 0, 1, sprite.pivot.x)
        const anchorY = gsap.utils.mapRange(-size.height / 2, size.height / 2, 0, 1, sprite.pivot.y)

        // Make sure we don't divide by zero
        if (!anchorX || anchorX === 1 || !anchorY || anchorY === 1) {
            return
        }

        const negativeXScaleFactor = size.width * anchorX
        const positiveXScaleFactor = size.width * (1 - anchorX)
        const negativeYScaleFactor = size.height * anchorY
        const positiveYScaleFactor = size.height * (1 - anchorY)

        const distanceToLeft = Math.abs(left - (sprite.x + this.x))
        const distanceToRight = Math.abs(sprite.x + this.x - right)
        const distanceToTop = Math.abs(top - (sprite.y + this.y))
        const distanceToBottom = Math.abs(bottom - (sprite.y + this.y))

        const leftScale = distanceToLeft / negativeXScaleFactor
        const rightScale = distanceToRight / positiveXScaleFactor
        const topScale = distanceToTop / negativeYScaleFactor
        const bottomScale = distanceToBottom / positiveYScaleFactor

        const scale = Math.max(leftScale, rightScale, topScale, bottomScale)
        this.backgroundSprite.scale.set(scale)
    }

    public setLoadProgress(progress: number): void {
        this.continue.setProgress(progress)
    }

    public loadComplete(): void {
        this.continue.cursor = 'pointer'
    }

    public async waitToDismiss(): Promise<void> {
        await flow.wait(this.continue.onPressed)

        // Skip the splash screen animations
        flow.skipStart()
        // Wait for the splash screen animations to finish (should be immediate)
        await this.currentPage?.hide()
        flow.skipStop()

        // Cleanup
        this.dismiss()
        this.continueOrientor.disconnect()
        this.onResizeConnection.disconnect()
        this.logo.destroy()
        this.logoOrientor.disconnect()
    }
}

export interface SplashSpec extends SplashSpecification {
    logo: SpineSpecification & OrientationMap<ContainerSpecification>
    background: SpriteSpecification & {
        size: { width: number; height: number }
    } & OrientationMap<ContainerSpecification>
    glow: SpineSpecification
    continue: OrientationMap<LoadingButtonSpec>
    page: SplashPageSpec
    antebetPage: AntebetSplashPageSpec
}
